import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import axios from "axios";
import toast from "react-hot-toast";
import { Clock, RotateCcw } from "lucide-react";
import { useModalDismiss } from "./useModalDismiss";
import { User, loadUsers, displayName, initials, avatarColor } from "./user-display";

interface Version {
    id: string;
    pageId: string;
    body: string;
    editorId: string | null;
    createdAt: string;
}

interface PageData {
    id: string;
    title: string;
    folderId: string | null;
    currentVersionId: string | null;
}

interface Props {
    page: PageData;
    isAdmin: boolean;
    onRestored: () => void;
    onClose: () => void;
}

export default function PageVersionsDrawer({ page, isAdmin, onRestored, onClose }: Props) {
    const { show, dismiss, onExited } = useModalDismiss(onClose, onRestored);
    const [versions, setVersions] = useState<Version[]>([]);
    const [users, setUsers] = useState<Map<string, User>>(new Map());
    const [selected, setSelected] = useState<Version | null>(null);
    const [loading, setLoading] = useState(true);
    const [restoring, setRestoring] = useState(false);

    useEffect(() => {
        setLoading(true);
        axios.get(`/api/manuals/pages/${page.id}/versions`)
            .then(res => {
                if (res.data.success) {
                    const items = (res.data.items as Version[]).sort(
                        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                    );
                    setVersions(items);
                    setSelected(items[0] || null);
                    loadUsers()
                        .then(list => setUsers(new Map(list.map(u => [u.id, u]))))
                        .catch(() => { });
                } else {
                    toast.error(res.data.message || "Failed to load versions");
                }
            })
            .catch(() => toast.error("Failed to load versions"))
            .finally(() => setLoading(false));
    }, [page.id]);

    const handleRestore = () => {
        if (!selected) return;
        setRestoring(true);
        axios.put(`/api/manuals/pages/${page.id}`, {
            folderId: page.folderId,
            title: page.title,
            body: selected.body,
        })
            .then(() => axios.post(`/api/manuals/pages/${page.id}/publish`))
            .then(res => {
                if (res.data.success) {
                    toast.success("Version restored");
                    dismiss("save");
                } else {
                    toast.error(res.data.message || "Failed to restore");
                    setRestoring(false);
                }
            })
            .catch(err => {
                toast.error(err.response?.data?.message || "Failed to restore");
                setRestoring(false);
            });
    };

    const isCurrent = (v: Version) => v.id === page.currentVersionId;

    return (
        <Modal show={show} onHide={() => dismiss("cancel")} onExited={onExited} centered size="xl" dialogClassName="m-versions-modal" backdropClassName="m-modal-backdrop">
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 17, fontWeight: 600 }}>Version history</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: 0 }}>
                <div className="m-versions-wrap">
                    <div className="m-versions-list">
                        {loading ? (
                            <div className="m-versions-empty">Loading…</div>
                        ) : versions.length === 0 ? (
                            <div className="m-versions-empty">No published versions yet</div>
                        ) : (
                            versions.map((v, i) => {
                                const isSel = selected?.id === v.id;
                                const label = isCurrent(v) ? "Current" : `Version ${versions.length - i}`;
                                const u = v.editorId ? users.get(v.editorId) : null;
                                const name = u ? displayName(u) : v.editorId ? v.editorId.slice(0, 8) : "Unknown";
                                return (
                                    <button
                                        key={v.id}
                                        className={`m-version-row${isSel ? " selected" : ""}`}
                                        onClick={() => setSelected(v)}
                                    >
                                        <div className="m-version-row-head">
                                            <span className="m-version-row-label">{label}</span>
                                            {isCurrent(v) && <span className="m-version-row-badge">live</span>}
                                        </div>
                                        <div className="m-version-row-meta">
                                            <Clock size={11} />
                                            <span>{formatRelative(v.createdAt)}</span>
                                        </div>
                                        <div className="m-version-row-author">
                                            <span
                                                className="m-comment-avatar sm"
                                                style={{ background: v.editorId ? avatarColor(v.editorId) : "#94a3b8" }}
                                            >
                                                {u ? initials(name) : "?"}
                                            </span>
                                            <span className="m-version-row-name">{name}</span>
                                        </div>
                                    </button>
                                );
                            })
                        )}
                    </div>
                    <div className="m-versions-preview">
                        {selected ? (
                            <>
                                <div className="m-versions-preview-head">
                                    <h3>{page.title}</h3>
                                    <span className="m-versions-preview-meta">
                                        {isCurrent(selected) ? "Current version" : `Saved ${formatRelative(selected.createdAt)}`}
                                    </span>
                                </div>
                                <div className="m-page-body" dangerouslySetInnerHTML={{ __html: selected.body }} />
                            </>
                        ) : (
                            <div className="m-versions-empty">Select a version to preview</div>
                        )}
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="m-btn m-btn-ghost" onClick={() => dismiss("cancel")}>
                    Close
                </button>
                {isAdmin && selected && !isCurrent(selected) && (
                    <button
                        type="button"
                        className="m-btn m-btn-primary"
                        onClick={handleRestore}
                        disabled={restoring}
                    >
                        <RotateCcw size={14} /> {restoring ? "Restoring…" : "Restore this version"}
                    </button>
                )}
            </Modal.Footer>
        </Modal>
    );
}

function formatRelative(iso: string): string {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;
    return d.toLocaleDateString();
}
